// FILE: systemSettingsClient.ts
// Purpose: HTTP wrapper around `/api/admin/system-settings/*`, mirroring
//          AgentsClient's shape (plain object, static methods) over `adminFetch`.
// Layer: Web client
// Exports: `SystemSettingsClient`

import type {
  AdminFilePreviewResponse,
  AdminGenerationDefaultsResponse,
  AdminQaAutomationResponse,
  AdminQaAutomationUpdateRequest,
  AdminThreadRecapResponse,
  FilePreviewSetting,
  GenerationDefaultsSetting,
  QaAutomationSetting,
  ThreadRecapSetting,
} from "@taskin-code/contracts";

import { adminFetch } from "./adminFetch";

const GENERATION_DEFAULTS_URL = "/api/admin/system-settings/generation-defaults";
const FILE_PREVIEW_URL = "/api/admin/system-settings/file-preview";
const THREAD_RECAP_URL = "/api/admin/system-settings/thread-recap";
const QA_AUTOMATION_URL = "/api/admin/system-settings/qa-automation";

export const SystemSettingsClient = {
  /**
   * `a.view`-gated. Always answers: an installation that has never been
   * configured returns the model the server will actually use, so the screen
   * never has to guess at a default of its own.
   */
  async readGenerationDefaults(): Promise<GenerationDefaultsSetting> {
    const result = await adminFetch<AdminGenerationDefaultsResponse>(GENERATION_DEFAULTS_URL);
    return result.generationDefaults;
  },

  /** `a.edit`-gated. Replaces the setting and echoes back what was stored. */
  async writeGenerationDefaults(
    value: GenerationDefaultsSetting,
  ): Promise<GenerationDefaultsSetting> {
    const result = await adminFetch<AdminGenerationDefaultsResponse>(GENERATION_DEFAULTS_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.generationDefaults;
  },

  /**
   * `a.view`-gated. Always answers: an installation that has never opted any
   * location in gets `{ allowedGlobs: [] }`, so the screen shows that nothing
   * is currently previewable rather than an empty field to guess at.
   */
  async readFilePreview(): Promise<FilePreviewSetting> {
    const result = await adminFetch<AdminFilePreviewResponse>(FILE_PREVIEW_URL);
    return result.filePreview;
  },

  /** `a.edit`-gated. Replaces the whole glob list and echoes back what was stored. */
  async writeFilePreview(value: FilePreviewSetting): Promise<FilePreviewSetting> {
    const result = await adminFetch<AdminFilePreviewResponse>(FILE_PREVIEW_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.filePreview;
  },

  /**
   * `a.view`-gated. Always answers: an installation that has never opted in
   * gets `{ enabled: false }`, which is what the server will actually do.
   */
  async readThreadRecap(): Promise<ThreadRecapSetting> {
    const result = await adminFetch<AdminThreadRecapResponse>(THREAD_RECAP_URL);
    return result.threadRecap;
  },

  /** `a.edit`-gated. Replaces the setting and echoes back what was stored. */
  async writeThreadRecap(value: ThreadRecapSetting): Promise<ThreadRecapSetting> {
    const result = await adminFetch<AdminThreadRecapResponse>(THREAD_RECAP_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.threadRecap;
  },

  /**
   * `a.view`-gated. The whole installation's record, indexed by project id: one
   * read answers "which projects are opted in", which is also the first thing
   * the server's own sweep asks.
   */
  async readQaAutomation(): Promise<QaAutomationSetting> {
    const result = await adminFetch<AdminQaAutomationResponse>(QA_AUTOMATION_URL);
    return result.qaAutomation;
  },

  /**
   * One project at a time, gated on `a.edit` **and** `u.assign_permissions`:
   * enabling this delegates to an account whatever people ask for from GitHub,
   * which is handing out authority rather than configuring what threads run.
   *
   * A `422` means the configuration was refused, and its `error` names what is
   * missing — a permission the actor lacks, a tool the agent lost. `adminFetch`
   * surfaces that text as the thrown message, so the screen can show it as is.
   */
  async writeQaAutomation(value: AdminQaAutomationUpdateRequest): Promise<QaAutomationSetting> {
    const result = await adminFetch<AdminQaAutomationResponse>(QA_AUTOMATION_URL, {
      method: "PUT",
      body: JSON.stringify(value),
    });
    return result.qaAutomation;
  },
};
